/**
 * This service uses stripe SDK
 * https://stripe.com/docs/terminal/references/api/js-sdk#stripeterminal-create
 */
import { loadStripeTerminal } from '@stripe/terminal-js'
import { computed, ref, reactive, watch } from 'vue'

const SIMULATOR = 'SIMULATOR'
const terminal = ref(null)

export async function createStripeSdk ({ getConnectionToken, handleUnexpectedTerminalDisconnect }) {
  if (terminal?.value) throw new Error('sdk was already called')

  const StripeTerminal = await loadStripeTerminal()

  terminal.value = StripeTerminal.create({
    onFetchConnectionToken: getConnectionToken,
    onUnexpectedReaderDisconnect: handleUnexpectedTerminalDisconnect,
  })

  const wifiCheckoutPending = ref(false)
  let unwatchSimulatorConfig = null

  const simulatorConfig = reactive({
    simulated: false,
    testCardNumber: null,
  })

  unwatchSimulatorConfig = watch(() => simulatorConfig, (conf) => {
    setSimulatedTerminal({ config: conf })
  }, { deep: true })

  // I still recommend extracting SDK methods, we should avoid using that directly if possible
  function setSimulatedTerminal ({ config, reader } = {}) {
    const { simulated, testCardNumber } = config || simulatorConfig
    if (simulated && testCardNumber) {
      terminal?.value?.setSimulatorConfiguration({ testCardNumber })
    }
  }

  async function connect (reader) {
    const connectResult = await terminal?.value?.connectReader(reader)

    const { simulated, testCardNumber } = simulatorConfig
    if (reader.id === SIMULATOR && simulated && testCardNumber) {
      await terminal?.value?.setSimulatorConfiguration({ testCardNumber })
    }

    return connectResult
  }

  async function disconnect () {
    await terminal?.value?.disconnectReader()
  }

  async function discoverReaders (data) {
    return terminal?.value?.discoverReaders(data)
  }

  const paymentStatus = computed(() => terminal?.value?.getPaymentStatus())

  const connectionStatus = computed(() => terminal?.value?.getConnectionStatus())

  async function collectPaymentMethod (clientSecret) {
    wifiCheckoutPending.value = true
    const collectResult = await terminal?.value?.collectPaymentMethod(clientSecret)

    const { error } = collectResult
    if (error) throw error

    return collectResult
  }

  async function processPayment (paymentIntent) {
    wifiCheckoutPending.value = false
    const processResult = await terminal?.value?.processPayment(paymentIntent)

    const { error } = processResult
    if (error) throw error

    return processResult
  }

  async function cancelCollectPaymentMethod () {
    await terminal?.value?.cancelCollectPaymentMethod()
  }

  function destroyService () {
    terminal.value = null
    wifiCheckoutPending.value = null
    simulatorConfig.simulated = null
    simulatorConfig.testCardNumber = null
    unwatchSimulatorConfig?.()
  }

  return {
    connect,
    disconnect,
    discoverReaders,
    collectPaymentMethod,
    processPayment,
    cancelCollectPaymentMethod,
    destroyService,
    paymentStatus,
    connectionStatus,
    simulatorConfig,
    wifiCheckoutPending,
    setSimulatedTerminal,
  }
}
